// components/onboarding/ProgressStepper.jsx - Enterprise Grade
import React from 'react';
import { motion } from 'framer-motion';

const steps = [
  { 
    id: 1, 
    name: 'Account Setup', 
    description: 'Create your account',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )
  },
  { 
    id: 2, 
    name: 'Business Info', 
    description: 'Tell us about your business',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    )
  },
  { 
    id: 3, 
    name: 'KYC Documents', 
    description: 'Upload verification docs',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  { 
    id: 4, 
    name: 'Review & Launch', 
    description: 'Start shipping instantly',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    )
  },
];

const ProgressStepper = ({ currentStep }) => {
  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;

  return (
    <div className="relative w-full py-12 px-4 bg-gradient-to-b from-gray-50 to-white">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/5 rounded-full blur-3xl" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-400/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* Desktop View */}
        <div className="hidden md:block">
          {/* Progress Bar Background */}
          <div className="absolute top-[60px] left-0 right-0 h-1 bg-gray-100 rounded-full mx-16" />
          
          {/* Animated Progress Bar */}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `calc(${progress}% - 128px)` }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute top-[60px] left-16 h-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-full shadow-lg"
          />

          {/* Steps */}
          <div className="flex items-start justify-between relative">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center relative group"
                style={{ width: '200px' }}
              >
                {/* Step Circle */}
                <motion.div
                  animate={{
                    scale: currentStep === step.id ? 1.1 : 1,
                  }}
                  className="relative z-10 mb-4"
                >
                  {/* Glow effect for active step */}
                  {currentStep === step.id && (
                    <motion.div
                      animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.2, 0.5] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur-xl"
                    />
                  )}
                  
                  {/* Circle */}
                  <div
                    className={`relative flex items-center justify-center w-16 h-16 rounded-full border-4 transition-all duration-300 ${
                      currentStep > step.id
                        ? 'bg-gradient-to-br from-green-500 to-emerald-600 border-white shadow-xl'
                        : currentStep === step.id
                        ? 'bg-gradient-to-br from-blue-600 to-indigo-600 border-white shadow-2xl shadow-blue-500/50'
                        : 'bg-white border-gray-200 shadow-md'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <motion.svg
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-7 h-7 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </motion.svg>
                    ) : (
                      <div className={currentStep >= step.id ? 'text-white' : 'text-gray-400'}>
                        {step.icon}
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Step Info */}
                <div className="text-center">
                  <p
                    className={`text-sm font-bold mb-1 transition-colors ${
                      currentStep >= step.id
                        ? 'text-gray-900'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.name}
                  </p>
                  <p
                    className={`text-xs transition-colors ${
                      currentStep >= step.id
                        ? 'text-gray-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.description}
                  </p>
                  {currentStep === step.id && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="mt-2 px-3 py-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold rounded-full"
                    >
                      In Progress
                    </motion.div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          {/* Progress Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-500">
                Step {currentStep} of {steps.length}
              </p>
              <h3 className="text-xl font-bold text-gray-900 mt-1">
                {steps[currentStep - 1]?.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {steps[currentStep - 1]?.description}
              </p>
            </div>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg">
              {steps[currentStep - 1]?.icon}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              className="h-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-full relative overflow-hidden"
            >
              {/* Animated shine effect */}
              <motion.div
                animate={{ x: ['0%', '200%'] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              />
            </motion.div>
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between mt-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentStep >= step.id
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 scale-125'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressStepper;
